import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, catchError, filter, map, of, shareReplay, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';
import { getUserDataLocationId } from '../shared/utils/auth-headers.util';

/**
 * CWS-1536: Payment Method on an active membership (change the card it renews on).
 *
 * Offered wherever the super-admin feature flag `update_payment_methods` is on for the company
 * and location; each company sets it apart for Way-billed and imported memberships. No
 * dependency on plan changes. The API re-checks it on every card call, at the membership's own
 * location.
 */
export type PaymentMethodAvailabilityReason = 'ok' | 'payment_method_disabled';

export interface PaymentMethodAvailability {
  /** The location asked about: the requested one, else the session's. */
  location_id: string | null;
  enabled: boolean;
  reason: PaymentMethodAvailabilityReason;
  /**
   * Imported (DRB, iWashPOS...) memberships offer it: the company's own setting for them, apart
   * from `enabled` (Way-billed). Their vendor keeps billing them; a card can only be saved, and
   * only for one with a gate order (see ImportedCardList).
   */
  imported_memberships?: boolean;
}

/** A card on the membership's Stripe customer, as svc-order lists it. */
export interface SavedCard {
  id: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  /** The card the membership renews on today. */
  is_current: boolean;
  is_expired: boolean;
}

/** Why a membership's renewal card cannot be changed (paymentMethodRules INELIGIBLE). */
export type PaymentMethodIneligibleReason =
  | 'imported_membership'
  | 'way_app_membership'
  | 'billing_not_linked'
  | 'membership_not_active'
  | 'cancellation_scheduled';

export interface SavedCardList {
  subscription_id: string;
  eligible: boolean;
  reason: PaymentMethodIneligibleReason | null;
  cards: SavedCard[];
  /**
   * The current card is the customer's default, not set on this membership, so another of the
   * customer's memberships can change it; saving it sets it on this one.
   */
  renewal_card_inherited?: boolean;
}

/**
 * svc-order's answer on a membership's renewal card: its card can be changed (the card it renews
 * on, null for none), or it is refused (a cash membership, the feature off at its location...).
 */
export type RenewalCardLookup =
  { eligible: true; card: SavedCard | null } | { eligible: false; card?: undefined };

/** Why no card can be saved for an imported membership (paymentMethodRules IMPORTED_CARD_INELIGIBLE). */
export type ImportedCardIneligibleReason =
  | 'not_imported_membership'
  | 'way_app_membership'
  | 'membership_not_active'
  | 'shop_not_linked'
  | 'shop_not_connected';

/**
 * The cards saved for an imported membership, on the Stripe account of the shop its gate order is
 * at. Its vendor keeps billing it, so none of them is charged here and none is current.
 * `eligible` is false for a membership with no gate order (the vendor handles everything) or whose
 * shop has no Stripe account; Payment method is not offered for those.
 */
export interface ImportedCardList {
  subscription_id: string;
  eligible: boolean;
  reason: ImportedCardIneligibleReason | null;
  /** The vendor's source tag, e.g. 'drb'. */
  billed_by: string | null;
  location_id: string | null;
  branch: string | null;
  cards: SavedCard[];
}

/** A SetupIntent on the membership's connected account, for the Add New Card fields. */
export interface CardSetup {
  client_secret: string;
  stripe_account_id: string;
}

/**
 * A finished Add New Card: the card to select. When the card typed was already on file (same
 * number and expiry), svc-order removes the copy and `duplicate` is true.
 */
export interface CardSetupResult {
  subscription_id: string;
  payment_method_id: string;
  duplicate: boolean;
}

export interface PaymentMethodUpdate {
  subscription_id: string;
  card: SavedCard;
}

/** A refusal's stable `code`, or UNKNOWN when the API gave none (network, 500, ...). */
export type PaymentMethodErrorCode =
  | PaymentMethodIneligibleReason
  | ImportedCardIneligibleReason
  | PaymentMethodAvailabilityReason
  | 'subscription_not_found'
  | 'stripe_unavailable'
  | 'card_not_found'
  | 'card_expired'
  | 'card_change_in_progress'
  | 'request_failed'
  | 'UNKNOWN';

/**
 * svc-order answers `{ status: false, code, message }`; older handlers nest it under `data`.
 * Without a code: 'UNKNOWN' for no answer or a server error (worth trying again),
 * 'request_failed' for any other refusal (a 403, a 429: trying again does not help).
 */
export function toPaymentMethodErrorCode(error: unknown): PaymentMethodErrorCode {
  const http = error instanceof HttpErrorResponse ? error : null;
  const code = http?.error?.code ?? http?.error?.data?.code;
  if (typeof code === 'string' && code) {
    return code as PaymentMethodErrorCode;
  }
  const status = http?.status ?? 0;
  return status === 0 || status >= 500 ? 'UNKNOWN' : 'request_failed';
}

@Injectable({ providedIn: 'root' })
export class SubscriptionPaymentMethodService {
  /** One availability check per location: switching location asks again. */
  private readonly availability = new Map<string, Observable<PaymentMethodAvailability | null>>();

  constructor(private api: ApiService) {}

  /** Without a location, the API answers for the session's location. */
  getAvailability(locationId?: string | null): Observable<ApiResponse<PaymentMethodAvailability>> {
    return this.api.get<PaymentMethodAvailability>(
      'order/subscription-payment-method/availability',
      locationId ? { location_id: locationId } : {}
    );
  }

  /**
   * Whether Payment method is switched on at the active location, asked once per location: the
   * lane pop-ups check it before looking up a card, so a switched-off feature costs them no
   * request. A failed check answers false and is asked again next time.
   */
  isEnabled(): Observable<boolean> {
    return this.sessionAvailability().pipe(map(availability => availability?.enabled === true));
  }

  /**
   * Whether imported memberships offer it (their vendor notice), from the same one check. The
   * company sets it apart from Way-billed memberships, so it does not depend on `enabled`.
   */
  isImportedEnabled(): Observable<boolean> {
    return this.sessionAvailability().pipe(
      map(availability => availability?.imported_memberships === true)
    );
  }

  private sessionAvailability(): Observable<PaymentMethodAvailability | null> {
    const locationId = getUserDataLocationId() ?? null;
    const key = locationId ?? '';
    let answer$ = this.availability.get(key);
    if (!answer$) {
      answer$ = this.getAvailability(locationId).pipe(
        map(response => response?.data ?? null),
        catchError(() => {
          this.availability.delete(key);
          return of(null);
        }),
        shareReplay(1)
      );
      this.availability.set(key, answer$);
    }
    return answer$;
  }

  /** The membership's saved cards, the current renewal card first. */
  listCards(subscriptionId: string): Observable<ApiResponse<SavedCardList>> {
    return this.api
      .get<SavedCardList>(`${this.base(subscriptionId)}/payment-methods`)
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  /**
   * "Card on file": the card the membership renews on, or null when it has none. Completes with
   * no value when the card cannot be shown here (Payment method off, not a customer manager, an
   * ineligible membership, or the lookup failed), so the caller leaves the field out.
   */
  renewalCard(subscriptionId: string): Observable<SavedCard | null> {
    return this.renewalCardLookup(subscriptionId).pipe(
      filter(lookup => lookup.eligible),
      map(lookup => lookup.card ?? null)
    );
  }

  /**
   * The card lookup, telling a refused membership apart, for a caller that offers Payment method
   * before it answers (it takes Stripe a second or two) and withdraws it on a refusal. Completes
   * with no value when the lookup failed in a way that may pass (no answer, Stripe down): the
   * pop-up then says so and offers Try again.
   */
  renewalCardLookup(subscriptionId: string): Observable<RenewalCardLookup> {
    return this.listCards(subscriptionId).pipe(
      map(response => response?.data),
      filter((list): list is SavedCardList => typeof list?.eligible === 'boolean'),
      map((list): RenewalCardLookup =>
        list.eligible
          ? { eligible: true, card: list.cards.find(card => card.is_current) ?? null }
          : { eligible: false }
      ),
      catchError((code: PaymentMethodErrorCode) =>
        code === 'UNKNOWN' || code === 'stripe_unavailable'
          ? EMPTY
          : of<RenewalCardLookup>({ eligible: false })
      )
    );
  }

  /**
   * Starts adding a card: a SetupIntent on the membership's Stripe customer, on the location's
   * connected account. The card is entered in Stripe's own fields and never reaches svc-order.
   */
  createCardSetup(subscriptionId: string): Observable<ApiResponse<CardSetup>> {
    return this.api
      .post<CardSetup>(`${this.base(subscriptionId)}/payment-methods/setup-intent`, {})
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  /**
   * Finishes adding a card once Stripe.js confirmed the SetupIntent: says which card to select,
   * and whether it was a copy of one already on file.
   */
  completeCardSetup(
    subscriptionId: string,
    setupIntentId: string
  ): Observable<ApiResponse<CardSetupResult>> {
    return this.api
      .post<CardSetupResult>(
        `${this.base(subscriptionId)}/payment-methods/setup-intent/${encodeURIComponent(setupIntentId)}/complete`,
        {}
      )
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  /** Makes a saved card the one the membership renews on from the next renewal. */
  setRenewalCard(
    subscriptionId: string,
    paymentMethodId: string
  ): Observable<ApiResponse<PaymentMethodUpdate>> {
    return this.api
      .put<PaymentMethodUpdate>(`${this.base(subscriptionId)}/payment-method`, {
        payment_method_id: paymentMethodId,
      })
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  /** The cards saved for an imported membership its vendor bills (never charged here). */
  listImportedCards(subscriptionId: string): Observable<ApiResponse<ImportedCardList>> {
    return this.api
      .get<ImportedCardList>(`${this.base(subscriptionId)}/imported-cards`)
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  /**
   * Whether a card can be saved for this imported membership: it has a gate order at a shop with
   * Stripe. False for one with no order, whose payments all happen in the vendor's system, and on
   * any failure, so Payment method stays hidden for it.
   */
  canSaveImportedCard(subscriptionId: string): Observable<boolean> {
    return this.listImportedCards(subscriptionId).pipe(
      map(response => response?.data?.eligible === true),
      catchError(() => of(false))
    );
  }

  /** Starts saving a card for an imported membership, on its shop's connected account. */
  createImportedCardSetup(subscriptionId: string): Observable<ApiResponse<CardSetup>> {
    return this.api
      .post<CardSetup>(`${this.base(subscriptionId)}/imported-cards/setup-intent`, {})
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  /** Finishes saving a card for an imported membership; answers as completeCardSetup does. */
  completeImportedCardSetup(
    subscriptionId: string,
    setupIntentId: string
  ): Observable<ApiResponse<CardSetupResult>> {
    return this.api
      .post<CardSetupResult>(
        `${this.base(subscriptionId)}/imported-cards/setup-intent/${encodeURIComponent(setupIntentId)}/complete`,
        {}
      )
      .pipe(catchError(error => throwError(() => toPaymentMethodErrorCode(error))));
  }

  private base(subscriptionId: string): string {
    return `order/subscription/${encodeURIComponent(subscriptionId)}`;
  }
}
